/**
 * Whitelist data for the paste-guard sanitizer.
 */

// Tags removed completely along with all children.
export const HARD_REJECT_TAGS = new Set([
    'script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base',
    'applet', 'frame', 'frameset', 'html', 'head', 'body', 'noscript',
    'template', 'slot'
]);

// Attributes permitted on every allowed tag.
export const GLOBAL_ATTRS = new Set(['dir', 'lang', 'style']);

// Allowed (non-MathML) tags mapped to additional per-tag attributes
// beyond the GLOBAL_ATTRS.
export const ALLOWED_TAGS: Record<string, string[]> = {
    // block
    p: ['align'],
    h1: [], h2: [], h3: [], h4: [], h5: [], h6: [],
    blockquote: ['cite'],
    hr: [],
    ul: ['start', 'type'],
    ol: ['start', 'type', 'reversed'],
    li: [],
    pre: [],
    // inline
    strong: [], b: [], em: [], i: [], u: [], s: [], strike: [], mark: [], code: [],
    sub: [], sup: [],
    br: [],
    span: ['data', 'class'], // 'data' + 'class' gated by tooltip carve-out in sanitize.ts
    // tables
    table: [], thead: [], tbody: [], tr: [],
    td: ['colspan', 'rowspan', 'align', 'scope'],
    th: ['colspan', 'rowspan', 'align', 'scope'],
    caption: [],
    // images ('class' is filtered against IMAGE_CLASS_PATTERN in sanitize.ts)
    img: ['src', 'alt', 'width', 'height', 'class', 'data-source', 'uuid', 'data-latex']
};

// Allowed tags that form their own block. 'figure' only stays with
// SanitizeOptions.allowFigures (see keepsFigure in sanitize.ts).
export const BLOCK_TAGS = new Set([
    'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'hr',
    'ul', 'ol', 'li', 'pre', 'table', 'figure'
]);

// Allowed tags that can hold paragraphs.
export const FLOW_CONTAINER_TAGS = new Set(['blockquote', 'li', 'td', 'th']);

// Unsupported block-level tags. They are unwrapped, but their content stays
// on its own line (see unwrapBlock in sanitize.ts).
export const UNWRAP_BLOCK_TAGS = new Set([
    'address', 'article', 'aside', 'center', 'dd', 'details', 'dialog', 'div',
    'dl', 'dt', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'header',
    'hgroup', 'legend', 'main', 'nav', 'section', 'summary'
]);

// Values of the dir attribute that an unwrapped block passes on to its content.
export const TEXT_DIRECTIONS = new Set(['ltr', 'rtl', 'auto']);

// Figures that CKEditor writes for block images (ImageCaption) and tables
// (TableCaption). With SanitizeOptions.allowFigures they keep these classes
// and their <figcaption>; any other figure is unwrapped.
export const IMAGE_FIGURE_CLASS = 'image';
export const TABLE_FIGURE_CLASS = 'table';
export const FIGURE_CLASS_PATTERN = /^(image|table|image_resized|image-style-[a-z-]+)$/;

// Classes kept on <img>: Wiris and calculator formulas (custom-plugins/wiris,
// custom-plugins/calculator; 'preload' marks a calculator image that
// BaseObjectDirective still renders) and the resize and style classes of
// CKEditor inline images. Any other class is dropped, because the CMS content
// goes through $compile, which runs class directives such as "ng-init: expr;".
export const IMAGE_CLASS_PATTERN = /^(wiris-image-singleline|wiris-image-multiline|image-from-calculator|preload|ck-math-widget|image_resized|image-style-[a-z-]+)$/;

// ImageResize writes the size of a resized image as a percentage width
// (config.image.resizeUnit, '%' by default) with this class, on the image
// figure for a block image and on the <img> for an inline image. Only that
// width is kept, up to 100% (see filterStyle in sanitize.ts).
export const RESIZED_IMAGE_CLASS = 'image_resized';
export const RESIZED_WIDTH_PATTERN = /^\d{1,3}(\.\d+)?%$/;

// Class that marks a span as a tooltip (custom-plugins/tooltip).
export const TOOLTIP_CLASS = 'ck5-tooltip';

// <a> is only allowed with SanitizeOptions.allowLinks; it keeps nothing but href.
export const LINK_ATTRS = ['href'];

// Tags whose class attribute is filtered against SanitizeOptions.allowedClasses.
export const STYLE_CLASS_TAGS = new Set(['p', 'span']);

// Attributes that mark an <img> as a Wiris (data-source) or calculator
// (data-latex) formula rendered into a data: URL.
export const FORMULA_IMG_ATTRS = ['data-source', 'data-latex'];

// The calculator and Wiris commands first insert a formula as a loader image
// with the 'preload' class and replace its src with a data: URL once MathJax
// has rendered it. When rendering never finishes, the loader stays in the
// answer and the PDF step renders it from its formula attributes
// (BaseObjectDirective.fixUnfinishedDesmosRenderings), so it is a formula too.
// The commands write the src as "../images/desmosLoader.gif". Only a path on
// this server is accepted, so a paste cannot load an image from another host.
export const FORMULA_LOADER_CLASS = 'preload';
export const FORMULA_LOADER_SRC = /^(\.\.?\/)*\/?images\/desmosLoader\.gif$/;

// MathML tags pass through with MATHML_ATTRS.
export const MATHML_TAGS = new Set([
    'math', 'mrow', 'mi', 'mn', 'mo', 'ms', 'mtext',
    'msub', 'msup', 'msubsup', 'mfrac', 'msqrt', 'mroot',
    'mstyle', 'merror', 'mpadded', 'mphantom', 'mfenced',
    'mmultiscripts', 'mprescripts', 'none', 'mtable', 'mtr', 'mtd',
    'munder', 'mover', 'munderover', 'mspace',
    'semantics', 'annotation', 'annotation-xml'
]);

export const MATHML_ATTRS = new Set([
    'mathvariant', 'mathsize', 'mathcolor', 'display',
    'linethickness', 'encoding', 'xmlns',
    // mfenced: the fences and separators are part of the formula.
    'open', 'close', 'separators'
]);

// Style property allowlist for the inline style attribute.
// IBPIT-1884: 'font-size' and 'font-family' deliberately omitted.
export const ALLOWED_STYLE_PROPS = new Set([
    'color', 'font-weight', 'font-style',
    'text-decoration', 'text-align',
    'vertical-align'
]);

// Inline styles that make an element invisible, as ib_admin/tools/flow_fix.py
// (IBPIT-1960) defines them for the response PDF: display:none,
// visibility:hidden, zero opacity, a transform that scales to zero, a
// clip:rect() with zero area, and a 0 or 1px box that clips its overflow.
// Desmos/MathQuill and WIRIS keep helper spans like this, for example the
// LaTeX source of a formula in <span class="dcg-mq-selectable"
// style="clip:rect(1em,1em,1em,1em);...">. A foreign paste drops the style, so
// the hidden text would show up in the answer (IBPIT-1888); such elements are
// removed instead (see isHidden in sanitize.ts). The properties are matched by
// their full name, so backface-visibility or --opacity does not count.
export const TRANSFORM_PROPS = ['transform', '-webkit-transform', '-moz-transform', '-ms-transform'];

// vertical-align keeps only these keywords. A length such as 3000px moves
// text out of its line and over other text (IBPIT-1889, IBPIT-1951).
export const VERTICAL_ALIGN_KEYWORDS = new Set([
    'baseline', 'sub', 'super', 'top', 'text-top', 'middle', 'bottom', 'text-bottom'
]);

// A pasted text colour must reach this contrast with the white answer box,
// the WCAG minimum for large text (see isReadableColor in sanitize.ts).
export const MIN_TEXT_CONTRAST = 3;

// Colour names accepted on pasted text, as hex. Word writes windowtext. Any
// other name is dropped and the text gets the default colour.
export const TEXT_COLOR_NAMES: Record<string, string> = {
    black: '#000000', windowtext: '#000000', white: '#ffffff',
    red: '#ff0000', darkred: '#8b0000', maroon: '#800000', brown: '#a52a2a',
    green: '#008000', darkgreen: '#006400', olive: '#808000', teal: '#008080',
    blue: '#0000ff', darkblue: '#00008b', navy: '#000080', purple: '#800080',
    gray: '#808080', grey: '#808080', dimgray: '#696969', dimgrey: '#696969'
};

// Attributes of own content (SanitizeOptions.ownContent) that hold a URL. Their
// value is checked like a pasted link or image source.
// Tags removed from own content on top of HARD_REJECT_TAGS: SVG animation can
// set an attribute such as href to a script URL.
export const OWN_CONTENT_REJECT_TAGS = new Set(['animate', 'animatemotion', 'animatetransform', 'set', 'discard']);

// URL schemes kept in own content. Relative and protocol-relative URLs are
// kept too; a data: URL only as an image (see isOwnContentUrl in sanitize.ts).
export const OWN_CONTENT_URL_SCHEMES = new Set(['http', 'https', 'mailto', 'tel', 'ftp']);

// Classes that a relaxed foreign paste keeps on span, p, mark and code, on
// top of allowedClasses and the tooltip class: the formatting that Highlight,
// FontSize and CodeBlock write as a class.
export const RELAXED_CLASS_PATTERN = /^((marker|pen)-[a-z]+|text-(tiny|small|big|huge)|language-[a-z0-9-]+)$/;

export const OWN_CONTENT_URL_ATTRS = new Set([
    'href', 'src', 'srcset', 'xlink:href', 'action', 'formaction', 'background', 'poster',
    'ping', 'cite', 'longdesc'
]);

// Formatting that a relaxed foreign paste keeps on top of ALLOWED_STYLE_PROPS
// (see isRelaxedStyle in sanitize.ts). Position, display, float, transform,
// width and height are not on it, because they break the layout of an answer
// (IBPIT-1884). A value with a function other than a colour, or with a
// backslash, is never kept.

// Properties with keyword or name values.
export const RELAXED_KEYWORDS: Record<string, RegExp> = {
    'font-family': /^[\w\s,'"-]{1,200}$/,
    'font-variant': /^(normal|small-caps)$/,
    'text-transform': /^(none|capitalize|uppercase|lowercase)$/,
    'border-collapse': /^(collapse|separate)$/,
    'list-style-type': /^[a-z-]{1,30}$/
};

// Properties with a length, and the lengths they may have in CSS pixels, so
// that text cannot shrink to nothing or move out of view. percent is the
// pixels that 1% stands for (of a 16px font, or of an answer box about
// 800px wide), or 0 where a percentage is not kept.
export interface LengthLimit {
    min: number;
    max: number;
    percent: number;
}
const BOX_SIDE: LengthLimit = { min: 0, max: 200, percent: 8 };
const PADDING: LengthLimit = { min: 0, max: 100, percent: 8 };
export const RELAXED_LENGTHS: Record<string, LengthLimit> = {
    'font-size': { min: 8, max: 72, percent: 0.16 },
    'line-height': { min: 8, max: 120, percent: 0.16 },
    'letter-spacing': { min: 0, max: 20, percent: 0 },
    'word-spacing': { min: 0, max: 40, percent: 0 },
    'text-indent': BOX_SIDE,
    'margin': BOX_SIDE, 'margin-top': BOX_SIDE, 'margin-right': BOX_SIDE, 'margin-bottom': BOX_SIDE, 'margin-left': BOX_SIDE,
    'padding': PADDING, 'padding-top': PADDING, 'padding-right': PADDING, 'padding-bottom': PADDING, 'padding-left': PADDING
};
export const BORDER_WIDTH: LengthLimit = { min: 0, max: 10, percent: 0 };
export const BORDER_STYLES = new Set(['none', 'hidden', 'solid', 'dashed', 'dotted', 'double', 'groove', 'ridge', 'inset', 'outset']);

// CSS pixels per unit; em, rem, ex and ch at a 16px font.
export const PX_PER_UNIT: Record<string, number> = {
    px: 1, pt: 4 / 3, pc: 16, in: 96, cm: 96 / 2.54, mm: 96 / 25.4, em: 16, rem: 16, ex: 8, ch: 8
};

// A kept background colour must leave dark text readable: its relative
// luminance, blended over white, is at least this (yellow is 0.93, orange
// 0.48, red 0.21).
export const MIN_BACKGROUND_LUMINANCE = 0.4;

// Colour names accepted as background colours, as hex, on top of
// TEXT_COLOR_NAMES: the light colours that highlighting uses.
export const BACKGROUND_COLOR_NAMES: Record<string, string> = {
    yellow: '#ffff00', lightyellow: '#ffffe0', gold: '#ffd700', orange: '#ffa500', khaki: '#f0e68c',
    lime: '#00ff00', lightgreen: '#90ee90', aqua: '#00ffff', cyan: '#00ffff', lightblue: '#add8e6',
    fuchsia: '#ff00ff', magenta: '#ff00ff', pink: '#ffc0cb', lightpink: '#ffb6c1', lavender: '#e6e6fa',
    silver: '#c0c0c0', lightgray: '#d3d3d3', lightgrey: '#d3d3d3', gainsboro: '#dcdcdc',
    whitesmoke: '#f5f5f5', beige: '#f5f5dc', transparent: '#ffffff00'
};

// Default MIME types accepted on data: URLs in img.src.
export const DEFAULT_IMAGE_MIME_TYPES = [
    'image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'
];

// Attribute name prefixes that are always stripped from every element.
export const ATTR_PREFIX_BLOCKLIST = ['on', 'ng-', 'data-ng-', 'data-cke-'];

// Exact attribute names that are always stripped.
export const ATTR_FULL_BLOCKLIST = new Set(['formaction', 'srcdoc']);

export const ATTR_PATTERN_BLOCKLIST = /^\*ng|^\[\(.*\)\]$|^\[.*\]$|^\(.*\)$/;

export interface SanitizeOptions {
    allowImageDataUrls?: boolean; // default true
    allowedImageMimeTypes?: string[]; // default DEFAULT_IMAGE_MIME_TYPES
    // When false, <img> is removed entirely on paste. Required in candidate
    // response mode: dragged/copied content images carry assessment-mode URLs
    // (media/<uuid>.png) that the PDF renderer rejects. Formula images
    // (FORMULA_IMG_ATTRS with a data: src, or the formula loader) are kept
    // regardless, so moving a formula inside an answer does not delete it.
    allowImg?: boolean; // default true
    // Keep <a href> (http(s), mailto, relative). Otherwise links are unwrapped.
    allowLinks?: boolean; // default false
    // Keep the image and table figures of the editor with their captions.
    // Image figures also need allowImg. Otherwise figures are unwrapped.
    allowFigures?: boolean; // default false
    // Keep h1 to h6. Otherwise a heading becomes a bold paragraph (see
    // headingToParagraph in sanitize.ts).
    allowHeadings?: boolean; // default true
    // Class names kept on STYLE_CLASS_TAGS, i.e. the editor's style definitions.
    allowedClasses?: string[]; // default []
    // The content is exactly a copy that an editor of this page with the same
    // rights put on the clipboard (copy, cut or drag; see
    // registerOwnContentMarker in handler.ts). Nothing on the clipboard can
    // claim it. It is not filtered against the whitelist; only scripts,
    // embedded documents, SVG animation, event handlers, URLs that can run
    // code, script-capable CSS and AngularJS bindings are removed. Set by the
    // paste guard per paste; the editor config cannot set it.
    ownContent?: boolean; // default false
    // Foreign content pasted while the paste filter is switched off (mode
    // 'relaxed', handler.ts): the whitelist applies as for any foreign paste,
    // but fonts, sizes, background colours, spacing, indents and borders stay
    // (isRelaxedStyle in sanitize.ts, RELAXED_CLASS_PATTERN). Layout does not. Set by
    // the paste guard per paste.
    relaxed?: boolean; // default false
}
